import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  DragIndicator,
  Image
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { categoryService } from '../services/api';
import CategoryForm from '../components/CategoryForm';

const Categories = () => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive'
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const includeInactive = filterStatus === 'all' || filterStatus === 'inactive';
      const response = await categoryService.getAll(includeInactive);
      let fetchedCategories = response.categories || [];

      // Sort by order
      fetchedCategories.sort((a, b) => a.order - b.order);

      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar(t('categories.failedToLoadCategories'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  // Get display name based on current language
  const getCategoryName = (category) => {
    return i18n.language === 'gr' ? category.nameGr : category.nameEn;
  };

  // Filter categories based on search and status
  const filteredCategories = categories.filter(category => {
    const matchesSearch = getCategoryName(category).toLowerCase().includes(search.toLowerCase());

    if (filterStatus === 'active') {
      return matchesSearch && category.isActive;
    } else if (filterStatus === 'inactive') {
      return matchesSearch && !category.isActive;
    }
    return matchesSearch;
  });

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingCategory) {
        await categoryService.update(editingCategory._id, formData);
        setSnackbar({
          open: true,
          message: t('categories.categoryUpdated'),
          severity: 'success'
        });
      } else {
        await categoryService.create(formData);
        setSnackbar({
          open: true,
          message: t('categories.categoryCreated'),
          severity: 'success'
        });
      }
      setFormOpen(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('common.error'),
        severity: 'error'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (category) => {
    setDeleteDialog({ open: true, category });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.category) {
      try {
        await categoryService.delete(deleteDialog.category._id);
        setSnackbar({
          open: true,
          message: t('categories.categoryDeleted'),
          severity: 'success'
        });
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        if (error.response?.data?.error === 'Cannot delete category with content') {
          const { videoCount, storyCount } = error.response.data;
          let message = t('categories.cannotDeleteCategoryWithContent') + '\n';
          if (videoCount > 0) {
            message += t('categories.categoryHasVideos', { count: videoCount }) + '\n';
          }
          if (storyCount > 0) {
            message += t('categories.categoryHasStories', { count: storyCount });
          }
          setSnackbar({
            open: true,
            message: message,
            severity: 'error'
          });
        } else {
          setSnackbar({
            open: true,
            message: t('common.error'),
            severity: 'error'
          });
        }
      }
    }
    setDeleteDialog({ open: false, category: null });
  };


  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Drag and drop handlers
  const handleDragStart = (e, category, index) => {
    setIsDragging(true);
    setDraggedItem({ category, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, droppedIndex) => {
    e.preventDefault();
    setIsDragging(false);

    if (!draggedItem || draggedItem.index === droppedIndex) {
      setDraggedItem(null);
      return;
    }

    const newCategories = [...filteredCategories];
    const [removed] = newCategories.splice(draggedItem.index, 1);
    newCategories.splice(droppedIndex, 0, removed);

    // Update order values
    const reorderedCategories = newCategories.map((cat, index) => ({
      id: cat._id,
      order: index + 1
    }));

    try {
      await categoryService.reorder(reorderedCategories);
      showSnackbar(t('categories.categoryOrderUpdated'));
      fetchCategories();
    } catch (error) {
      showSnackbar(t('common.error'), 'error');
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedItem(null);
  };

  const getIconDisplay = (category) => {
    // Use iconFullUrl from API if available, otherwise fallback to manual construction
    let fullIconUrl = null;

    if (category.iconFullUrl) {
      fullIconUrl = category.iconFullUrl;
    } else if (category.iconUrl) {
      const CDN_URL = process.env.REACT_APP_CDN_URL || 'https://cdn.worshipcoffee.eu';
      fullIconUrl = category.iconUrl.startsWith('http')
        ? category.iconUrl
        : `${CDN_URL}/${category.iconUrl}`;
    }

    if (fullIconUrl) {
      return (
        <Avatar
          src={fullIconUrl}
          sx={{ width: 40, height: 40 }}
          variant="rounded"
        />
      );
    } else {
      return (
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.300' }} variant="rounded">
          <Image />
        </Avatar>
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            {t('categories.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('categories.manageCategoriesDesc')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddCategory}
          size="large"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {t('categories.add')}
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('common.search')}
          value={search}
          onChange={handleSearch}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('categories.filterBy')}</InputLabel>
          <Select
            value={filterStatus}
            onChange={handleFilterChange}
            label={t('categories.filterBy')}
            sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}
          >
            <MenuItem value="all">{t('categories.filterAll')}</MenuItem>
            <MenuItem value="active">{t('categories.filterActive')}</MenuItem>
            <MenuItem value="inactive">{t('categories.filterInactive')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table Section */}
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ width: 80 }}>{t('categories.icon')}</TableCell>
              <TableCell>{t('categories.name')}</TableCell>
              <TableCell sx={{ width: 100 }}>{t('categories.order')}</TableCell>
              <TableCell sx={{ width: 120 }}>{t('categories.status')}</TableCell>
              <TableCell sx={{ width: 150 }}>{t('categories.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {t('common.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {t('categories.noCategories')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category, index) => (
                <TableRow
                  key={category._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, category, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: 'move',
                    opacity: isDragging && draggedItem?.category._id === category._id ? 0.5 : 1,
                    backgroundColor: isDragging ? 'action.hover' : 'inherit',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <TableCell>
                    <IconButton size="small" sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                      <DragIndicator />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {getIconDisplay(category)}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {getCategoryName(category)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i18n.language === 'gr' ? category.nameEn : category.nameGr}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{category.order}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category.isActive ? t('categories.isActive') : t('categories.inactive')}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('categories.edit')}>
                        <IconButton onClick={() => handleEditCategory(category)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('categories.delete')}>
                        <IconButton onClick={() => handleDeleteClick(category)} size="small" color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category Form Dialog */}
      <CategoryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleFormSubmit}
        category={editingCategory}
        loading={formLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, category: null })}>
        <DialogTitle>{t('categories.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('categories.confirmDeleteCategory')}
          </DialogContentText>
          {deleteDialog.category && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {getCategoryName(deleteDialog.category)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.language === 'gr' ? deleteDialog.category.nameEn : deleteDialog.category.nameGr}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, category: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;